import React from 'react';

function formatColones(valor: string | number): string {
  const n = Number(valor || 0);
  return `₡${n.toLocaleString('es-CR')}`;
}

function formatHoraCierre(hora: string | null | undefined): string {
  if (!hora) return '—';
  const [h, m] = hora.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return hora;
  return new Date(2026, 0, 1, h, m).toLocaleTimeString('es-CR', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

export interface JugadaLocal {
  id: string;
  sorteo_id: string;
  sorteo_nombre: string;
  sorteo_hora_cierre: string | null;
  num1: string;
  monto: number;
  montoRev2: number;
  esReventado: boolean;
}

interface AddedBetsPanelProps {
  jugadas: JugadaLocal[];
  totalActual: number;
  enviando: boolean;
  sorteosDisponibles: boolean;
  onEliminar: (id: string) => void;
  onLimpiar: () => void;
  onCrearTicket: () => void;
}

export const AddedBetsPanel: React.FC<AddedBetsPanelProps> = ({
  jugadas,
  totalActual,
  enviando,
  sorteosDisponibles,
  onEliminar,
  onLimpiar,
  onCrearTicket,
}) => {
  if (jugadas.length === 0) return null;

  // Group by sorteo
  const jugadasPorSorteo = jugadas.reduce<Record<string, JugadaLocal[]>>((acc, j) => {
    if (!acc[j.sorteo_id]) acc[j.sorteo_id] = [];
    acc[j.sorteo_id].push(j);
    return acc;
  }, {});

  return (
    <div className="space-y-3">
      {/* Floating summary bar */}
      <div className="bg-gray-900 rounded-2xl border border-gray-700 overflow-hidden">
        <div className="px-4 py-3 flex items-center justify-between border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-sm">
              🎫
            </div>
            <div>
              <p className="text-white font-bold text-sm">
                Jugadas agregadas ({jugadas.length})
              </p>
              <p className="text-xs text-gray-500">
                Total a jugar: <span className="text-amber-400 font-bold">{formatColones(totalActual)}</span>
              </p>
            </div>
          </div>
          <button onClick={onLimpiar} className="text-xs text-red-500 hover:text-red-400">
            Limpiar
          </button>
        </div>

        {/* Jugadas list */}
        <div className="max-h-48 overflow-y-auto">
          {Object.entries(jugadasPorSorteo).map(([sorteoId, grupo]) => (
            <div key={sorteoId}>
              <div className="px-4 py-1.5 bg-gray-800/40 flex items-center justify-between">
                <p className="text-[10px] text-amber-300 font-bold uppercase">{grupo[0]?.sorteo_nombre}</p>
                <p className="text-[10px] text-gray-500">{formatHoraCierre(grupo[0]?.sorteo_hora_cierre)}</p>
              </div>
              {grupo.map((j) => (
                <div key={j.id} className="px-4 py-2 flex items-center justify-between border-b border-gray-800/50">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-white font-bold text-base">{j.num1}</span>
                    <div className="text-xs">
                      {j.esReventado ? (
                        <>
                          {j.monto > 0 && <span className="text-blue-400 mr-2">D:{formatColones(j.monto)}</span>}
                          {j.montoRev2 > 0 && <span className="text-purple-400">R:{formatColones(j.montoRev2)}</span>}
                        </>
                      ) : (
                        <span className="text-blue-400">{formatColones(j.monto)}</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => onEliminar(j.id)}
                    className="text-gray-600 hover:text-red-400 text-lg w-8 h-8 flex items-center justify-center"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Revisar button */}
        <div className="p-3">
          <button
            type="button"
            onClick={onCrearTicket}
            disabled={enviando || !sorteosDisponibles}
            className="w-full py-4 bg-green-600 hover:bg-green-500 active:bg-green-700 disabled:bg-gray-700 disabled:text-gray-500 text-white font-bold text-base rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            {enviando ? 'Creando ticket...' : (
              <>
                Revisar y continuar
                <span className="text-lg">→</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
